'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect';
import { detectPlatform, formatShortcut } from '@/lib/workspace/commands';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Compass, Grid2X2, LogIn } from 'lucide-react';
import { Avatar } from '@/components/avatar';
import { Tip } from '@/components/ui/tooltip';
import { useTranslation } from '@/components/i18n-provider';
import { useNotifications } from '@/hooks/useNotifications';
import { publishUnreadNotifications } from '@/components/workspace/unread-notifications';
import { useWorkspaceAnon } from '@/components/workspace/anon-context';
import { useFullscreenNav } from '@/components/workspace/fullscreen-nav';
import { SearchEverything } from '@/components/workspace/search-everything';
import {
  useDesktopFullscreenShownId,
  useFullscreenItemId,
  useHandsetFullscreen,
  useOverviewMode,
  usePendingFullscreen,
  usePresentedWindowId,
  useWorkspaceActions,
  useWorkspaceItems,
} from '@/components/workspace/store';
import {
  openWorkspacePanel,
  toggleWorkspacePanel,
  closeWorkspacePanel,
  useWorkspacePanelOpen,
  useWorkspacePanelTab,
} from '@/components/workspace/panels/panel-events';
import { useIsMd } from '@/components/workspace/panels/panel-shell';
import { useHandsetLandscape } from '@/hooks/use-handset-landscape';
import { makeFetchWithGuestMint } from '@/lib/api/fetch-with-guest-mint';
import { startGuest } from '@/lib/auth/guest-client';
import { executeTurnstile } from '@/lib/auth/turnstile-execute';
import { isGuestUser } from '@/lib/auth/is-guest';
import { emitWorkspaceListChanged } from '@/lib/workspaces/list-events';
import { useRailAutohide } from '@/lib/workspaces/rail-flag';
import { FULLSCREEN_RESTORING_ATTR } from '@/lib/workspaces/screen-state';
import { cn } from '@/lib/utils';
import type { UserMenuUser } from '@/components/user-menu-dropdown';
import { RailItem, RailLayoutContext } from './rail-item';
import { RailDock } from './rail-dock';
import {
  requestDiscoverToggle,
  useDiscoverOpen,
} from '@/components/workspace/discover/discover-events';

/**
 * The left rail (V2, collapsed by E1 + U2). Reserves a liquid-glass column
 * so the canvas is a clean rectangle. Five fixed affordances: brand (top),
 * then the navigation pair Discover + Stage, the per-screen taskbar, and
 * the badged avatar at the foot (the ONE person cluster: unread rides the
 * avatar, Notifications is the first row of the panel it opens). The former
 * panel views (Search / Spaces / Apps / Agents / Content / Feed /
 * Collaborate / the bell) are absorbed: Discover carries the field, catalog,
 * files and feed; Stage carries the workspace switcher, Share, who-is-here.
 *
 * Visual language is bound by §0 of the rail-redesign doc: the existing
 * `.liquid-glass` surface on the container, the real Robutler logo,
 * `lucide-react` icons, and a calm, minimal, flat item surface.
 */
/** Reserved rail column, in px. Keep in step with the `w-[56px]` classes
 *  below — this value is what the canvas reserves via `--ws-rail-w`.
 *  Exported because the `/w/` share page has to predict the canvas box it is
 *  about to navigate into (`canvasViewport`), and measuring 1710 when the
 *  canvas is 1654 sized every added window 56px too wide (2026-09-21). */
export const RAIL_WIDTH_PX = 56;

export function WorkspaceRail({ user }: { user: UserMenuUser | null }) {
  const authed = !!user;
  const router = useRouter();
  const { t } = useTranslation();
  const { anonReadOnly, sourceWorkspaceId } = useWorkspaceAnon();
  const { openHref } = useFullscreenNav();
  const fetchGated = useMemo(() => makeFetchWithGuestMint(), []);
  const { unreadCount } = useNotifications(authed);
  // The bar of a full-screen app shows this same number on its ⋯
  // (unread-notifications.ts): this rail draws no column there, so its own
  // badge is not on the page. `null` when there is no one to notify. The
  // second effect runs only as the rail leaves the page, so a page without a
  // rail never shows a count left over from one that had it.
  useEffect(() => {
    publishUnreadNotifications(authed ? unreadCount : null);
  }, [authed, unreadCount]);
  useEffect(() => () => publishUnreadNotifications(null), []);
  const discoverOpen = useDiscoverOpen();
  const railItems = useWorkspaceItems();
  // An empty workspace has no screens — Stage is a verb with no object, so
  // its button waits until the first app lands (Discover stays alone at the
  // top, pressed, over its fullscreen greeting). The ⌘⇧E chord stays live.
  const { addItem } = useWorkspaceActions();
  // A widget fullscreen takeover (green maximize). The takeover portal
  // (z-200) covers the rail entirely; fullscreen chrome now lives in the
  // top app header (Esc-hold reveal, see fullscreen-chrome.tsx). The rail
  // only keeps `fsActive` to mute its ⌘⇧ chords — toggling panels UNDER
  // the takeover would be invisible state churn.
  const fullscreenItemId = useFullscreenItemId();
  const fsActive = !!fullscreenItemId;
  // Overview (screens v2): the bottom-cluster Overview item lights while
  // the camera is pulled back; toggled via ws:screen / ⌘⇧E.
  const overviewMode = useOverviewMode();
  // Personal auto-hide preference: collapse the rail to a hover hot-edge
  // so the canvas runs full-width (toggled from the account menu).
  const [autohide] = useRailAutohide();

  // Panel-open + active-view state, read from the shared mirror (never a
  // local copy — several rail items target `left`, so the lit item =
  // "that panel is open AND its active view matches this item's tab").
  // Computed HERE (before the auto-hide layout) because auto-hide depends
  // on it: when a docked panel is OPEN, the rail must PIN (reserve its
  // column) so the panel sits BESIDE it — otherwise the hidden rail slides
  // in UNDER the panel, or its hover hot-edge is covered by the panel.
  const openByPanel: Record<string, boolean> = {
    left: useWorkspacePanelOpen('left'),
    right: useWorkspacePanelOpen('right'),
    bottom: useWorkspacePanelOpen('bottom'),
  };
  const activeTabByPanel: Record<string, string | undefined> = {
    left: useWorkspacePanelTab('left'),
    right: useWorkspacePanelTab('right'),
    bottom: useWorkspacePanelTab('bottom'),
  };
  const anyPanelOpen =
    openByPanel.left || openByPanel.right || openByPanel.bottom;
  // Effective auto-hide: with the saved preference, an open panel PINS the rail
  // (so the panel sits beside it, not under it). A fullscreen widget needs no
  // special-casing here — its portal (z-200) covers the rail either way.
  const effAutohide = autohide && !anyPanelOpen;
  // Mobile (<768px): the bottom bar is the navigation. EXCEPT on a
  // LANDSCAPE HANDSET: a rotated phone has desktop-like width and scarce
  // height, so it renders THIS desktop rail (logo, Discover, Stage,
  // taskbar, avatar) as a reserved left column - the full reel, not a
  // reduced bar.
  const isMd = useIsMd();
  const handsetLandscape = useHandsetLandscape();
  // Avoid a desktop flash of the mobile drawer: `useIsMd` starts false
  // (SSR-safe) and only flips after mount, so we treat "not yet mounted"
  // as desktop and switch to the drawer only once mobile is confirmed.
  const [railMounted, setRailMounted] = useState(false);
  // Layout effect: the mobile/desktop branch decision must land before the
  // hydrated frame paints — with a plain effect, phones painted the DESKTOP
  // rail first and then swapped to the bottom bar (the load-time
  // vertical/horizontal flip).
  useIsomorphicLayoutEffect(() => setRailMounted(true), []);
  // The phone's in-tab full screen on a ROTATED handset: the rail steps
  // aside with the (already absent) bottom navigation while a window is
  // presented, so the app takes the rail's column (handoff 2026-09-29,
  // section 4.5). Per device, from the store's local flag; a desktop never
  // sets it.
  const handsetFullscreen = useHandsetFullscreen();
  const presentedWindow = usePresentedWindowId();
  const landscapeFullscreenHidden = railMounted && handsetLandscape && handsetFullscreen && !!presentedWindow;
  // DESKTOP FULL SCREEN (lane D, 2026-09-29; the Stage algorithm, rule 14:
  // "the rail and the title bar hide"). While the view is on a window's
  // temporary full-screen screen the rail steps aside and gives its column
  // back, so the app fills the canvas edge to edge under its app bar. The
  // chords stay live (the listeners below are bound whatever renders): the
  // ⌘⇧ digits and Stage move between screens in full screen too (rule 17).
  // A desktop only; a handset never shows a window this way.
  const desktopFullscreenShown = useDesktopFullscreenShownId();
  const desktopFullscreenHidden = railMounted && isMd && !handsetLandscape && !!desktopFullscreenShown;
  // THE HAND-OVER FROM THE STYLESHEET (2026-10-03). When a tab reloads in full
  // screen, a script in the page's head marks `<html>` before the first paint
  // and a rule in globals.css draws no rail on a marked page, so the ordinary
  // layout never flashes while the canvas brings the full screen back
  // (lib/workspaces/screen-state.ts). This component takes the mark off as
  // it takes over: once the full screen is on view, where the branch below
  // draws no column itself, or once a request for one has come and gone
  // without it (the window was closed since), which is when the rail is due
  // back. The script's own timer covers a page where neither ever happens.
  const pendingFullscreen = usePendingFullscreen();
  const sawFullscreenRequest = useRef(false);
  useEffect(() => {
    if (pendingFullscreen) sawFullscreenRequest.current = true;
    const settledWithout = sawFullscreenRequest.current && !pendingFullscreen;
    if (desktopFullscreenHidden || settledWithout) {
      document.documentElement.removeAttribute(FULLSCREEN_RESTORING_ATTR);
    }
  }, [pendingFullscreen, desktopFullscreenHidden]);
  // Expose the reserved rail width so workspace-centered overlays (the
  // command palette + the empty-canvas search) can center in the canvas
  // area to the right of the rail rather than on the whole viewport.
  useIsomorphicLayoutEffect(() => {
    // Auto-hide reserves no column (the rail is a fully-hidden overlay), so
    // the canvas reclaims the full width — UNLESS a panel is open, in which
    // case the rail pins and reserves its column (effAutohide). Layout
    // effect so the var is correct before first paint.
    const px = desktopFullscreenHidden
      ? 0
      : (railMounted && !isMd && !handsetLandscape) || landscapeFullscreenHidden
        ? 0
        : effAutohide
          ? 0
          : RAIL_WIDTH_PX;
    document.documentElement.style.setProperty('--ws-rail-w', `${px}px`);
    return () => {
      document.documentElement.style.removeProperty('--ws-rail-w');
    };
  }, [railMounted, isMd, handsetLandscape, effAutohide, landscapeFullscreenHidden, desktopFullscreenHidden]);

  // `wide` = the mobile drawer's full-width icon+label rows. The DESKTOP rail
  // is always the compact vertical icon-over-label (~64px) signed-off look.
  // (`labeled` retained as an alias for the few spots that mean "wide".)
  const wide = railMounted && !isMd && !handsetLandscape;
  const labeled = wide;

  // Enabling auto-hide should hide the OPEN panel too, not just the rail —
  // otherwise the canvas isn't actually clean. Fires on the autohide toggle.
  useEffect(() => {
    if (autohide && !wide) closeWorkspacePanel('left');
  }, [autohide, wide]);

  // Same gesture-driven mint as the create FAB, and it shipped with the same
  // hole: no Turnstile token, so in production `/api/auth/guest` refused every
  // one of these and the visitor was bounced to /signup instead of getting the
  // guest canvas the click promised. The challenge is usually silent.
  //
  // In-flight guard, mirroring the splash's "Open app" button: adding the
  // challenge turned this handler from milliseconds into seconds, which is long
  // enough for a second click to land. Two clicks meant two `startGuest` calls,
  // so the visitor got a second throwaway account and a second cloned
  // workspace, and the two navigations raced over which canvas they landed on.
  // The ref is what actually guards (state from the click's own render is stale
  // by the time the await returns); the state only dims the item.
  const anonBusy = useRef(false);
  const [anonPending, setAnonPending] = useState(false);
  const anonHandler = async () => {
    if (anonBusy.current) return;
    anonBusy.current = true;
    setAnonPending(true);
    const turnstileToken = await executeTurnstile().catch(() => null);
    const r = await startGuest({
      sourceWorkspaceId: sourceWorkspaceId ?? undefined,
      turnstileToken,
    });
    if (r.ok && r.workspaceId) {
      // Keep the guard closed through the navigation: the page is being torn
      // down, and a click in that window would mint a second guest.
      window.location.assign(`/workspace/${r.workspaceId}`);
    } else {
      // No navigation here: `openHref` is an in-page dialog, so the rail item
      // has to come back or a refused mint (429 / Turnstile) would leave the
      // only sign-up affordance dead for the rest of the session.
      anonBusy.current = false;
      setAnonPending(false);
      openHref('/signup');
    }
  };

  // A GUEST is authed (has a user) but on a reserved `guest-` namespace and
  // gets deleted after 14 days. Unlike a true-anon (`anonHandler` starts a
  // guest), a guest's CTA UPGRADES the account in place via the signup dialog.
  const guest = isGuestUser(user);
  const goSignup = () => openHref('/signup');

  // Toggle semantics for the remaining left-panel items (Notifications /
  // Account): re-clicking the ACTIVE view closes the panel; clicking a
  // different one switches the view.
  const togglePanelTab = (tab: string) => {
    const isActiveTab = (openByPanel.left ?? false) && activeTabByPanel.left === tab;
    if (isActiveTab) {
      toggleWorkspacePanel('left');
      return;
    }
    openWorkspacePanel('left', { tab });
  };

  // ---- Global rail chords --------------------------------------------------
  //
  // ⌘⇧K → Discover toggle (the plain ⌘K palette stays);
  // ⌘⇧E → overview toggle; ⌘⇧1…9 → go to screen N in taskbar order
  // (the digit of the CURRENT screen hops back to the previous one).
  // Screens v2 handed the digits to the apps — the library panels are
  // one click or a ⌘⇧K away. Digits are matched on `e.code` because
  // Shift remaps `e.key` to '!', '@', … on US layouts. The same chords
  // also arrive relayed from widget iframes via the
  // `workspace:host-key` CustomEvent (WidgetBridge ← sdk.v2.js
  // forwarder) — key events never cross the iframe boundary on their
  // own. Everything is inert while a widget holds the fullscreen
  // takeover: a camera move / panel toggle UNDER the takeover would be
  // invisible state churn.
  //
  // `togglePanelTab` closes over per-render panel state, so the stable
  // listeners read it through a ref instead of re-binding document
  // listeners every render.
  const platform = useMemo(() => detectPlatform(), []);
  const chordCtxRef = useRef({ togglePanelTab, fsActive });
  chordCtxRef.current = { togglePanelTab, fsActive };
  useEffect(() => {
    const dispatchScreen = (detail: { kind: string; index?: number }) => {
      window.dispatchEvent(new CustomEvent('ws:screen', { detail }));
    };
    const runChord = (key: string, code: string): boolean => {
      const ctx = chordCtxRef.current;
      if (ctx.fsActive) return false;
      if (key === 'k' || key === 'K') {
        // ⌘⇧K is Discover's chord (a toggle). The host decides whether to
        // open the overlay or focus the already-showing inline mount. The
        // plain ⌘K palette is untouched.
        requestDiscoverToggle();
        return true;
      }
      if (key === 'e' || key === 'E' || code === 'KeyE') {
        dispatchScreen({ kind: 'overview-toggle' });
        return true;
      }
      const m = /^Digit([1-9])$/.exec(code);
      if (!m) return false;
      dispatchScreen({ kind: 'index', index: Number(m[1]) });
      return true;
    };
    const onKey = (e: KeyboardEvent) => {
      const mod = platform === 'mac' ? e.metaKey : e.ctrlKey;
      if (!mod || !e.shiftKey || e.altKey) return;
      if (runChord(e.key, e.code)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onHostKey = (e: Event) => {
      const d = (e as CustomEvent<{ key?: string; code?: string }>).detail;
      if (d && (d.key || d.code)) runChord(d.key ?? '', d.code ?? '');
    };
    document.addEventListener('keydown', onKey);
    window.addEventListener('workspace:host-key', onHostKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('workspace:host-key', onHostKey);
    };
  }, [platform]);

  // While the chord modifiers (⌘⇧ / Ctrl+Shift) are HELD, each rail item
  // shows a keycap badge for the key that completes its chord — a
  // just-in-time cheat sheet. keyup of EITHER modifier (its own keyup
  // event reports the modifier as released) or a window blur (Cmd-Tab
  // away, focus dropping into a widget iframe) clears it.
  const [chordHeld, setChordHeld] = useState(false);
  useEffect(() => {
    const modOf = (e: KeyboardEvent) =>
      platform === 'mac' ? e.metaKey : e.ctrlKey;
    const onDown = (e: KeyboardEvent) => {
      if (modOf(e) && e.shiftKey && !e.altKey) setChordHeld(true);
    };
    const onUp = (e: KeyboardEvent) => {
      if (!modOf(e) || !e.shiftKey) setChordHeld(false);
    };
    const clear = () => setChordHeld(false);
    document.addEventListener('keydown', onDown);
    document.addEventListener('keyup', onUp);
    window.addEventListener('blur', clear);
    return () => {
      document.removeEventListener('keydown', onDown);
      document.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', clear);
    };
  }, [platform]);
  const keycapsOn = chordHeld && !fsActive;

  // Group separator — pure breathing room, no visible hairline.
  const divider = <span aria-hidden className="my-1 block h-1.5 w-full" />;

  const railBody = (
    <>
      {/* Brand mark (top). The account menu + workspace switcher now live
          in the avatar at the bottom of the rail. */}
      <Tip label={authed ? 'Discover' : 'Robutler'} side="right">
        {/* Signed IN, the brand is an in-app affordance: it opens Discover,
            the front door to apps, agents and your files. Sending someone who
            is already working straight out to the marketing page is a dead
            end they have to navigate back from. Signed OUT it stays a link
            home — for a visitor on a shared workspace that IS the useful
            destination. */}
        <a
          href="/"
          aria-label={authed ? t('nav.discover') : t('workspace.rail.robutlerHome')}
          onClick={
            authed
              ? (e) => {
                  e.preventDefault();
                  requestDiscoverToggle();
                }
              : undefined
          }
          className={cn(
            'inline-flex h-9 items-center rounded-xl text-foreground/80 transition-colors hover:bg-accent/60 hover:text-foreground',
            labeled ? 'w-full gap-2 px-2.5' : 'w-full justify-center',
          )}
        >
          <Image
            src="/icons/Logo_B.svg"
            alt=""
            width={22}
            height={22}
            className="block shrink-0 dark:hidden"
          />
          <Image
            src="/icons/Logo_W.svg"
            alt=""
            width={22}
            height={22}
            className="hidden shrink-0 dark:block"
          />
          {labeled && (
            <>
              <Image
                src="/icons/Wm_B.svg"
                alt="Robutler"
                width={84}
                height={12}
                className="block dark:hidden"
              />
              <Image
                src="/icons/Wm_W.svg"
                alt="Robutler"
                width={84}
                height={12}
                className="hidden dark:block"
              />
            </>
          )}
        </a>
      </Tip>

      {divider}

      {/* Discover — the one surface where apps, agents and your own work
          live. Opens full width over the canvas; the same component renders
          inline on an empty screen, in which case this press focuses its
          field instead of opening a second copy. Reachable signed out. */}
      <RailItem
        icon={Compass}
        label={t('nav.discover')}
        hideLabel
        hint={formatShortcut({ key: 'k', mod: true, shift: true }, platform)}
        keycap={keycapsOn ? 'K' : undefined}
        active={discoverOpen}
        testId="workspace-rail-discover"
        onClick={() => requestDiscoverToggle()}
      />

      {/* Stage — the screens view, beside Discover: the two go-places verbs
          share the top navigation cluster (VS Code's convention: views top,
          identity bottom). Named STAGE everywhere user-facing. ICON-ONLY
          (user call, overruling the NN/g labels default): with two nav items
          the label wasn't disambiguating — "Stage" explains as little as the
          glyph — so the rail leans on tooltips, chord keycaps, and the
          mobile bar's labeled tabs for the naming. */}
      {(
        <RailItem
          icon={Grid2X2}
          label={t('workspace.rail.stage')}
          hideLabel
          hint={formatShortcut({ key: 'e', mod: true, shift: true }, platform)}
          keycap={keycapsOn ? 'E' : undefined}
          active={overviewMode}
          testId="workspace-rail-overview"
          onClick={() =>
            window.dispatchEvent(
              new CustomEvent('ws:screen', {
                detail: { kind: 'overview-toggle' },
              }),
            )
          }
        />
      )}

      {/* E1: the panel-view buttons (Search / Spaces / Apps / Agents /
          Content / Feed) are GONE — Discover absorbed all of them (field +
          ⌘K palette, catalog, My files, community feed) and Stage carries
          the workspace switcher. The LeftPanel views themselves survive for
          deep links and the mobile bar's Alerts / Account. */}

      {divider}

      {/* Taskbar — one entry per screen (every open app, minimized ones
          dimmed), in the shared screen order. Click = go there; hold ⌘⇧
          to see the digit chords. Grown from the old minimized-apps dock. */}
      {/* `flex-1 min-h-0` hands the taskbar every pixel between the nav
          cluster and the avatar — the dock measures this box to decide how
          many pucks fit before it has to collapse into the "…" flyout. */}
      <div className="flex w-full min-h-0 flex-1 flex-col">
        <RailDock chordHeld={keycapsOn} />
      </div>

      {/* Bottom cluster — Overview above the account avatar (U2: ONE person
          cluster). Collaborate is retired from the rail (who-is-here and
          invite live on Stage); the BELL is retired too — the unread badge
          rides the avatar, and Notifications is the first row of the
          Account panel the avatar opens. */}
      <div className="mt-auto flex w-full flex-col items-center gap-1 pt-1">
        {authed && user ? (
          <>
            {/* Guest conversion CTA — the single persistent Sign-up
                affordance, below Notifications and above the avatar. Neutral
                rail-item style (matches the nav items) but keeps its "Sign up"
                label visible so it still reads as an action, not just an icon. */}
            {guest && (
              <RailItem
                icon={LogIn}
                label={t('home.cta.signUp')}
                testId="workspace-rail-guest-signup"
                onClick={goSignup}
              />
            )}
            <button
              type="button"
              onClick={() => togglePanelTab('account')}
              aria-label={
                unreadCount > 0
                  ? `Account · ${unreadCount} unread notifications`
                  : 'Account'
              }
              aria-pressed={
                (openByPanel.left ?? false) && activeTabByPanel.left === 'account'
              }
              data-testid="workspace-rail-account"
              className={cn(
                'relative mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary/50',
                (openByPanel.left ?? false) && activeTabByPanel.left === 'account'
                  ? 'bg-accent/70'
                  : 'hover:bg-accent/60',
              )}
            >
              <Avatar
                seed={user.username}
                type={user.accountType === 'agent' ? 'agent' : 'human'}
                src={user.avatarUrl}
                className="h-8 w-8 shrink-0"
              />
              {/* The bell's badge, relocated (U2): same chip, new anchor. */}
              {unreadCount > 0 && (
                <span
                  aria-hidden
                  className="absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-background"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          </>
        ) : (
          <RailItem
            icon={LogIn}
            label={t('home.cta.signUp')}
            testId="workspace-rail-signup"
            onClick={anonHandler}
            disabled={anonPending}
          />
        )}
      </div>

      {/* Command-palette host — renders portals only (no in-flow chrome). */}
      <SearchEverything headless />
    </>
  );

  // Desktop full screen (see `desktopFullscreenHidden`): no column, the
  // palette host only, the same headless shape as the branch below.
  if (desktopFullscreenHidden) {
    return (
      <>
        <div className="w-0 shrink-0" aria-hidden data-testid="workspace-rail-fullscreen" />
        <SearchEverything headless />
      </>
    );
  }

  // Mobile (<768px) — off-canvas drawer behind a left-edge handle. The
  // rail reserves no column (canvas runs full-width); tap the handle to
  // open, tap the backdrop or any panel view to close. We use a tappable
  // handle rather than relying on an edge swipe, which iOS/Android claim
  // for back-navigation.
  //
  // A LANDSCAPE handset in the phone's in-tab full screen hides this rail
  // too (handoff 2026-09-29, section 4.5; wireframes v3 A, "Landscape full
  // screen": the rail hides and the app takes its width, the app bar stays).
  // Same shape as the mobile branch: no column, the palette host only.
  if (wide || landscapeFullscreenHidden) {
    // Mobile: the BOTTOM BAR (mobile-tab-bar.tsx) is the navigation —
    // Discover · Screens · Alerts · You, one tap each. The old edge handle
    // and its drawer strip are GONE: everything the strip carried is either
    // a bar slot, absorbed by Discover (apps, agents, files, search, feed),
    // or on Stage (the workspace switcher, Share). The left-panel sheet
    // still mounts for the views the bar opens (Alerts, Account).
    return (
      <>
        <div className="w-0 shrink-0" aria-hidden />
        {/* Command-palette host — renders portals only. */}
        <SearchEverything headless />
      </>
    );
  }

  // Desktop — pinned reserved column (optionally widened to show labels),
  // or an auto-hide hover overlay.
  return (
    <RailLayoutContext.Provider value={{ variant: 'compact' }}>
      <div
        // What the pre-paint rule in globals.css hides while a remembered
        // full screen is on its way back (see the hand-over effect above).
        data-ws-rail=""
        className={cn(
          'group relative h-full shrink-0',
          // Pre-hydration guard: below md this rail is CSS-hidden unless JS
          // has confirmed a landscape handset — otherwise phones paint the
          // vertical rail for the first server-rendered frames and then
          // swap to the bottom bar (the load-time flip).
          !handsetLandscape && 'max-md:hidden',
          // An open panel pins the rail (reserves its column) so the panel docks
          // beside it; only a panel-less canvas truly hides the rail.
          // (A fullscreen takeover simply covers the rail — its portal sits
          // at z-200 and the top app header is the fullscreen chrome now.)
          effAutohide ? 'w-0' : 'w-[56px]',
        )}
      >
        {effAutohide && (
          // Invisible left-edge hover zone — the rail is fully hidden, so this
          // sliver is what the user moves onto to reveal it (group-hover).
          <div
            aria-hidden
            className="pointer-events-auto absolute left-0 top-0 bottom-0 z-30 w-2.5"
          />
        )}
        <nav
          aria-label={t('workspace.canvas.workspace')}
          data-testid="workspace-rail"
          data-autohide={effAutohide ? '' : undefined}
          className={cn(
            'liquid-glass pointer-events-auto flex h-full w-[56px] flex-col items-stretch gap-1 px-1 py-2 select-none',
            // Pinned: a normal reserved column (translucent glass against the
            // canvas edge is fine). Auto-hide: a left OVERLAY that slides over
            // canvas content on hover — make it fully opaque (`ws-panel-solid`)
            // so the widgets behind it don't bleed through.
            effAutohide
              ? 'ws-panel-solid absolute left-0 top-0 z-40 -translate-x-full shadow-xl transition-transform duration-200 group-hover:translate-x-0 focus-within:translate-x-0'
              : 'relative z-30',
          )}
        >
          {railBody}
        </nav>
      </div>
    </RailLayoutContext.Provider>
  );
}
